import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Checkbox,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    Space,
    Table,
    Tabs,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { api } from '../../common/api';
import { useRequest } from '../../hooks';
import './ReportPage.css';

type DetailPayload = {
    report?: any;
    shareholders?: any[];
    legalChanges?: any[];
    taxInfo?: any;
    invoiceInfo?: any;
    suppliers?: any[];
    customers?: any[];
};

type PageData<T> = {
    total: number;
    list: T[];
};

const toDayjsValue = (value?: string | null): Dayjs | null => {
    if (!value) return null;
    const d = dayjs(value);
    return d.isValid() ? d : null;
};

const splitLines = (text?: string) =>
    text ? text.split(/\r?\n/).map(line => line.trim()).filter(Boolean) : [];

const joinLines = (lines?: string[]) => (lines && lines.length ? lines.join('\n') : '');

const numberOrUndefined = (value: any) => (value === null || value === undefined ? undefined : value);

type ReportBasicSaveRequest = {
    id?: number;
    sn: string;
    companyName: string;
    reportDate?: string;
    legalName?: string;
    legalAge?: number;
    legalShareRatio?: number;
    establishDate?: string;
    creditCode?: string;
    industry?: string;
    orgType?: string;
    region?: string;
    backgroundDesktop?: string;
    backgroundMobile?: string;
    reportCreated?: string;
    shareholders: any[];
    legalChanges: any[];
};

export const ReportPage: React.FC = () => {
    const request = useRequest();
    const [list, setList] = useState<any[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState<DetailPayload | null>(null);

    const [basicForm] = Form.useForm();
    const [taxForm] = Form.useForm();
    const [invoiceForm] = Form.useForm();
    const [partnerForm] = Form.useForm();

    const columns = useMemo(() => [
        { title: '企业名称', dataIndex: 'companyName', key: 'companyName' },
        { title: '报告编号', dataIndex: 'sn', key: 'sn' },
        {
            title: '报告日期',
            dataIndex: 'reportDate',
            key: 'reportDate',
            render: (value: string) => (value ? value.substring(0, 10) : '--')
        },
        {
            title: '操作',
            key: 'action',
            render: (_: any, record: any) => (
                <Space>
                    <Button type="link" onClick={() => fetchDetail(record.id)}>编辑</Button>
                    <Button type="link" danger onClick={() => handleDelete(record.id)}>删除</Button>
                    <Button type="link" onClick={() => openPublicPage(record.sn)}>查看前台</Button>
                </Space>
            )
        }
    ], []);

    const openPublicPage = (sn: string) => {
        const url = `/index.html?sn=${encodeURIComponent(sn)}`;
        window.open(url, '_blank');
    };

    const fetchList = async (page = pagination.current, pageSize = pagination.pageSize) => {
        setLoading(true);
        try {
            const body = {
                start: (page - 1) * pageSize,
                length: pageSize,
                isTotal: true
            };
            const res = await request.post(api.report.list, body, undefined, true);
            if (!res.status) {
                throw new Error(res.msg || '加载失败');
            }
            const pageData = res.data as PageData<any>;
            const nextList = pageData?.list || [];
            setList(nextList);
            setPagination({ current: page, pageSize, total: pageData?.total || 0 });
            if ((!detail || !detail.report) && nextList.length > 0) {
                fetchDetail(nextList[0].id);
            }
        } catch (err: any) {
            message.error(err.message || '加载列表失败');
        } finally {
            setLoading(false);
        }
    };

    const fetchDetail = async (id: number) => {
        try {
            const res = await request.get(`${api.report.detail}?id=${id}`, true);
            if (!res.status) {
                throw new Error(res.msg || '获取详情失败');
            }
            setDetail(res.data);
        } catch (err: any) {
            message.error(err.message || '获取详情失败');
        }
    };

    const handleDelete = async (id: number) => {
        if (!id) return;
        try {
            const res = await request.get(api.report.delete+"?id="+id);

            if(res.status){
                message.success('删除成功');
                setDetail(null);
                basicForm.resetFields();
                taxForm.resetFields();
                invoiceForm.resetFields();
                partnerForm.resetFields();
            }
            fetchList();
        } catch (err: any) {
            message.error(err.msg || '删除失败');
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    useEffect(() => {
        if (!detail) {
            return;
        }
        fillForms(detail);
    }, [detail]);

    const fillForms = (data: DetailPayload) => {
        const report = data.report || {};
        basicForm.setFieldsValue({
            id: report.id,
            sn: report.sn,
            companyName: report.companyName,
            reportDate: toDayjsValue(report.reportDate),
            legalName: report.legalName,
            legalAge: report.legalAge,
            legalShareRatio: report.legalShareRatio,
            establishDate: toDayjsValue(report.establishDate),
            creditCode: report.creditCode,
            industry: report.industry,
            orgType: report.orgType,
            region: report.region,
            backgroundDesktop: report.backgroundDesktop,
            backgroundMobile: report.backgroundMobile,
            reportCreated: toDayjsValue(report.reportCreated),
            shareholders: (data.shareholders || []).map(item => ({
                ...item
            })),
            legalChanges: (data.legalChanges || []).map(item => ({
                id: item.id,
                changeType: item.changeType,
                changeDate: toDayjsValue(item.changeDate),
                beforeInfoText: joinLines(item.beforeInfo),
                afterInfoText: joinLines(item.afterInfo)
            }))
        });

        taxForm.setFieldsValue({
            id: report.id,
            taxCreditLevel: data.taxInfo?.creditLevel,
            taxpayerType: data.taxInfo?.taxpayerType,
            taxVatSales12m: numberOrUndefined(data.taxInfo?.vatSales12m),
            taxVatSales24m: numberOrUndefined(data.taxInfo?.vatSales24m),
            taxArrearTimes: data.taxInfo?.arrearTimes,
            taxPaid12m: numberOrUndefined(data.taxInfo?.taxPaid12m),
            taxArrearBalance: numberOrUndefined(data.taxInfo?.arrearBalance),
            taxPaid24m: numberOrUndefined(data.taxInfo?.taxPaid24m),
            taxLateFee3m: numberOrUndefined(data.taxInfo?.lateFee3m),
            taxVatPayable12m: numberOrUndefined(data.taxInfo?.vatPayable12m),
            taxLateFee12m: numberOrUndefined(data.taxInfo?.lateFee12m),
            taxZeroMonths12m: data.taxInfo?.zeroDeclareMonths,
            taxLateFeeTimes: data.taxInfo?.lateFeeTimes,
            taxZeroStreak12m: data.taxInfo?.zeroDeclareStreak,
            taxLateFeeDesc: data.taxInfo?.lateFeeDesc,
            taxPenaltyTip: data.taxInfo?.penaltyTip,
            assetAmount: numberOrUndefined(data.taxInfo?.assetAmount),
            debtRatio: numberOrUndefined(data.taxInfo?.debtRatio),
            netProfit: numberOrUndefined(data.taxInfo?.netProfit),
            netProfitRatio: numberOrUndefined(data.taxInfo?.netProfitRatio),
            taxPenalties: (data.taxInfo?.penalties || []).map((item: any) => ({
                ...item,
                registerDate: toDayjsValue(item.registerDate)
            })),
            taxMonthly: (data.taxInfo?.monthly || []).map((item: any) => ({
                ...item
            }))
        });

        invoiceForm.setFieldsValue({
            id: report.id,
            invoiceRecentRecord: data.invoiceInfo?.recentRecord,
            invoiceMonthAmount: numberOrUndefined(data.invoiceInfo?.monthlyAmount),
            invoiceGrowth3m: numberOrUndefined(data.invoiceInfo?.growth3m),
            invoiceAmount3m: numberOrUndefined(data.invoiceInfo?.amount3m),
            invoiceGrowth6m: numberOrUndefined(data.invoiceInfo?.growth6m),
            invoiceAmount6m: numberOrUndefined(data.invoiceInfo?.amount6m),
            invoiceGrowth12m: numberOrUndefined(data.invoiceInfo?.growth12m),
            invoiceAmount12m: numberOrUndefined(data.invoiceInfo?.amount12m),
            invoiceAmount24m: numberOrUndefined(data.invoiceInfo?.amount24m),
            invoiceCustomer3m: data.invoiceInfo?.customer3m,
            invoiceCustomer12m: data.invoiceInfo?.customer12m,
            invoiceCount12m: data.invoiceInfo?.invoiceCount12m,
            invoiceVoidRatio12m: numberOrUndefined(data.invoiceInfo?.voidRatio12m),
            invoiceAvgGrowth12m: numberOrUndefined(data.invoiceInfo?.avgGrowth12m),
            invoiceRedRatioQty12m: numberOrUndefined(data.invoiceInfo?.redQtyRatio12m),
            invoiceRedRatioAmt12m: numberOrUndefined(data.invoiceInfo?.redAmtRatio12m),
            invoiceBreakMonths12m: data.invoiceInfo?.breakMonths12m,
            invoiceBreakMonthsExFeb: data.invoiceInfo?.breakMonthsExFeb,
            invoiceMaxGapDays: data.invoiceInfo?.maxGapDays,
            invoiceMonthly: (data.invoiceInfo?.monthly || []).map((item: any) => ({
                ...item
            }))
        });

        partnerForm.setFieldsValue({
            id: report.id,
            suppliers: data.suppliers || [],
            customers: data.customers || []
        });
    };

    const handleBasicSave = async () => {
        try {
            const values = await basicForm.validateFields();
            const payload: ReportBasicSaveRequest = {
                id: values.id,
                sn: values.sn,
                companyName: values.companyName,
                reportDate: values.reportDate ? values.reportDate.format('YYYY-MM-DD') : undefined,
                legalName: values.legalName,
                legalAge: values.legalAge,
                legalShareRatio: values.legalShareRatio,
                establishDate: values.establishDate ? values.establishDate.format('YYYY-MM-DD') : undefined,
                creditCode: values.creditCode,
                industry: values.industry,
                orgType: values.orgType,
                region: values.region,
                backgroundDesktop: values.backgroundDesktop,
                backgroundMobile: values.backgroundMobile,
                reportCreated: values.reportCreated ? values.reportCreated.format('YYYY-MM-DD') : undefined,
                shareholders: (values.shareholders || []).map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    ratio: item.ratio,
                    amount: item.amount
                })),
                legalChanges: (values.legalChanges || []).map((item: any) => ({
                    id: item.id,
                    changeType: item.changeType,
                    changeDate: item.changeDate ? item.changeDate.format('YYYY-MM-DD') : undefined,
                    beforeInfo: splitLines(item.beforeInfoText),
                    afterInfo: splitLines(item.afterInfoText)
                }))
            };
            const res = await request.post(api.report.saveBasic, payload, undefined, true);
            if (!res.status) {
                throw new Error(res.msg || '保存失败');
            }
            message.success('基本信息已保存');
            setDetail(res.data);
            fetchList();
        } catch (err: any) {
            message.error(err.message || '保存失败');
        }
    };

    const handleTaxSave = async () => {
        try {
            const values = await taxForm.validateFields();
            const payload = {
                ...values,
                taxPenalties: (values.taxPenalties || []).map((item: any) => ({
                    ...item,
                    registerDate: item.registerDate ? item.registerDate.format('YYYY-MM-DD') : undefined
                }))
            };
            const res = await request.post(api.report.saveTax, payload, undefined, true);
            if (!res.status) {
                throw new Error(res.msg || '保存失败');
            }
            message.success('纳税信息已保存');
            setDetail(res.data);
        } catch (err: any) {
            message.error(err.message || '保存失败');
        }
    };

    const handleInvoiceSave = async () => {
        try {
            const values = await invoiceForm.validateFields();
            const res = await request.post(api.report.saveInvoice, values, undefined, true);
            if (!res.status) {
                throw new Error(res.msg || '保存失败');
            }
            message.success('发票信息已保存');
            setDetail(res.data);
        } catch (err: any) {
            message.error(err.message || '保存失败');
        }
    };

    const handlePartnerSave = async () => {
        try {
            const values = await partnerForm.validateFields();
            const res = await request.post(api.report.savePartners, values, undefined, true);
            if (!res.status) {
                throw new Error(res.msg || '保存失败');
            }
            message.success('供应商/客户信息已保存');
            setDetail(res.data);
        } catch (err: any) {
            message.error(err.message || '保存失败');
        }
    };

    const handleCreate = () => {
        setDetail(null);
        basicForm.resetFields();
        taxForm.resetFields();
        invoiceForm.resetFields();
        partnerForm.resetFields();
        message.info('已切换至新增模式，请填写内容后保存');
    };

    const renderFormListHeader = (title: string, extra?: React.ReactNode) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <strong>{title}</strong>
            {extra}
        </div>
    );

    const columnSummary = (
        <Card style={{ marginTop: 16 }} title="税票报告列表" extra={<Button onClick={handleCreate}>新增报告</Button>}>
            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: (page, pageSize) => fetchList(page, pageSize)
                }}
            />
        </Card>
    );

    const renderShareholderList = () => (
        <>
            {renderFormListHeader('股东明细', (
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => {
                    const shareholders = basicForm.getFieldValue('shareholders') || [];
                    basicForm.setFieldsValue({ shareholders: [...shareholders, {}] });
                }}>
                    添加
                </Button>
            ))}
            <Form.List name="shareholders">
                {(fields, { remove }) => (
                    <>
                        {fields.map(field => (
                            <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 12 }}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'name']}
                                    fieldKey={[field.fieldKey, 'name']}
                                    rules={[{ required: true, message: '请输入名称' }]}
                                >
                                    <Input placeholder="股东名称" style={{ width: 200 }} />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'ratio']}
                                    fieldKey={[field.fieldKey, 'ratio']}
                                    rules={[{ required: true, message: '请输入参股比例' }]}
                                >
                                    <InputNumber placeholder="参股比例(%)" style={{ width: 150 }} />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'amount']}
                                    fieldKey={[field.fieldKey, 'amount']}
                                >
                                    <InputNumber placeholder="投资金额(元)" style={{ width: 180 }} />
                                </Form.Item>
                                <MinusCircleOutlined onClick={() => remove(field.name)} />
                            </Space>
                        ))}
                    </>
                )}
            </Form.List>
        </>
    );

    const renderLegalChangeList = () => (
        <>
            {renderFormListHeader('法人/股东变更', (
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => {
                    const legalChanges = basicForm.getFieldValue('legalChanges') || [];
                    basicForm.setFieldsValue({ legalChanges: [...legalChanges, {}] });
                }}>
                    添加
                </Button>
            ))}
            <Form.List name="legalChanges">
                {(fields, { remove }) => (
                    <>
                        {fields.map(field => (
                            <Card key={field.key} size="small" style={{ marginBottom: 12 }}>
                                <Space align="baseline" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Form.Item
                                        {...field}
                                        label="变更类型"
                                        name={[field.name, 'changeType']}
                                        fieldKey={[field.fieldKey, 'changeType']}
                                        style={{ flex: 1, marginRight: 12 }}
                                        rules={[{ required: true, message: '请输入类型' }]}
                                    >
                                        <Input placeholder="例如：法定代表人信息" />
                                    </Form.Item>
                                    <Form.Item
                                        {...field}
                                        label="变更日期"
                                        name={[field.name, 'changeDate']}
                                        fieldKey={[field.fieldKey, 'changeDate']}
                                    >
                                        <DatePicker />
                                    </Form.Item>
                                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                                </Space>
                                <Form.Item
                                    {...field}
                                    label="变更前"
                                    name={[field.name, 'beforeInfoText']}
                                >
                                    <Input.TextArea rows={2} placeholder="每行一条记录" />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    label="变更后"
                                    name={[field.name, 'afterInfoText']}
                                >
                                    <Input.TextArea rows={2} placeholder="每行一条记录" />
                                </Form.Item>
                            </Card>
                        ))}
                    </>
                )}
            </Form.List>
        </>
    );

    const renderMonthlyList = (fieldName: string) => (
        <>
            {renderFormListHeader('月度明细', (
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => {
                    const list = (fieldName === 'taxMonthly' ? taxForm : invoiceForm).getFieldValue(fieldName) || [];
                    (fieldName === 'taxMonthly' ? taxForm : invoiceForm).setFieldsValue({ [fieldName]: [...list, {}] });
                }}>
                    添加
                </Button>
            ))}
            <Form.List name={fieldName}>
                {(fields, { remove }) => (
                    <>
                        {fields.map(field => (
                            <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'year']}
                                    fieldKey={[field.fieldKey, 'year']}
                                    rules={[{ required: true, message: '年份' }]}
                                >
                                    <InputNumber placeholder="年份" style={{ width: 120 }} />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'month']}
                                    fieldKey={[field.fieldKey, 'month']}
                                    rules={[{ required: true, message: '月份' }]}
                                >
                                    <InputNumber placeholder="月份" style={{ width: 120 }} min={1} max={12} />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'amount']}
                                    fieldKey={[field.fieldKey, 'amount']}
                                >
                                    <InputNumber placeholder="金额(元)" style={{ width: 180 }} />
                                </Form.Item>
                                <MinusCircleOutlined onClick={() => remove(field.name)} />
                            </Space>
                        ))}
                    </>
                )}
            </Form.List>
        </>
    );

    const renderPenaltyList = () => (
        <>
            {renderFormListHeader('税务处罚', (
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => {
                    const penalties = taxForm.getFieldValue('taxPenalties') || [];
                    taxForm.setFieldsValue({ taxPenalties: [...penalties, {}] });
                }}>
                    添加
                </Button>
            ))}
            <Form.List name="taxPenalties">
                {(fields, { remove }) => (
                    <>
                        {fields.map(field => (
                            <Card key={field.key} size="small" style={{ marginBottom: 12 }}>
                                <Space align="baseline" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Form.Item
                                        {...field}
                                        label="登记日期"
                                        name={[field.name, 'registerDate']}
                                        fieldKey={[field.fieldKey, 'registerDate']}
                                    >
                                        <DatePicker />
                                    </Form.Item>
                                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                                </Space>
                                <Form.Item
                                    {...field}
                                    label="违法信息"
                                    name={[field.name, 'illegalInfo']}
                                >
                                    <Input.TextArea rows={2} placeholder="违法事实" />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    label="处理状态"
                                    name={[field.name, 'penaltyStatus']}
                                >
                                    <Input placeholder="如：责令限期缴纳税款" />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    label="备注"
                                    name={[field.name, 'detail']}
                                >
                                    <Input placeholder="备注信息" />
                                </Form.Item>
                            </Card>
                        ))}
                    </>
                )}
            </Form.List>
        </>
    );

    const renderPartnerList = (fieldName: 'suppliers' | 'customers', title: string) => (
        <>
            {renderFormListHeader(title, (
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => {
                    const list = partnerForm.getFieldValue(fieldName) || [];
                    partnerForm.setFieldsValue({ [fieldName]: [...list, {}] });
                }}>
                    添加
                </Button>
            ))}
            <Form.List name={fieldName}>
                {(fields, { remove }) => (
                    <>
                        {fields.map(field => (
                            <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 10 }}>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'rankNo']}
                                    fieldKey={[field.fieldKey, 'rankNo']}
                                    rules={[{ required: true, message: '排名' }]}
                                >
                                    <InputNumber placeholder="排名" style={{ width: 100 }} />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'name']}
                                    fieldKey={[field.fieldKey, 'name']}
                                    rules={[{ required: true, message: '名称' }]}
                                >
                                    <Input placeholder="名称" style={{ width: 220 }} />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'amount']}
                                    fieldKey={[field.fieldKey, 'amount']}
                                >
                                    <InputNumber placeholder="金额" style={{ width: 150 }} />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'ratio']}
                                    fieldKey={[field.fieldKey, 'ratio']}
                                >
                                    <InputNumber placeholder="占比(%)" style={{ width: 130 }} />
                                </Form.Item>
                                <Form.Item
                                    {...field}
                                    name={[field.name, 'related']}
                                    fieldKey={[field.fieldKey, 'related']}
                                    valuePropName="checked"
                                >
                                    <Checkbox>关联方</Checkbox>
                                </Form.Item>
                                <MinusCircleOutlined onClick={() => remove(field.name)} />
                            </Space>
                        ))}
                    </>
                )}
            </Form.List>
        </>
    );

    const detailTabs = (
        <Card style={{ marginTop: 16 }}>
            <Tabs
                items={[
                    {
                        key: 'basic',
                        label: '基本信息',
                        children: (
                            <>
                                <Form layout="vertical" form={basicForm}>
                                    <Form.Item name="id" hidden><Input /></Form.Item>
                                    <Divider orientation="left">报告信息</Divider>
                                    <Form.Item label="报告编号" name="sn" rules={[{ required: true, message: '请输入报告编号' }]}>
                                        <Input placeholder="请输入报告编号" />
                                    </Form.Item>
                                    <Form.Item label="企业名称" name="companyName" rules={[{ required: true, message: '请输入企业名称' }]}>
                                        <Input placeholder="请输入企业名称" />
                                    </Form.Item>
                                    <Space size="large" wrap>
                                        <Form.Item label="报告日期" name="reportDate">
                                            <DatePicker />
                                        </Form.Item>
                                        <Form.Item label="法人姓名" name="legalName">
                                            <Input placeholder="法人姓名" />
                                        </Form.Item>
                                        <Form.Item label="法人年龄" name="legalAge">
                                            <InputNumber placeholder="年龄" style={{ width: 120 }} />
                                        </Form.Item>
                                        <Form.Item label="法人占股比例(%)" name="legalShareRatio">
                                            <InputNumber placeholder="占股比例" style={{ width: 150 }} />
                                        </Form.Item>
                                        <Form.Item label="成立日期" name="establishDate">
                                            <DatePicker />
                                        </Form.Item>
                                    </Space>
                                    <Space size="large" wrap>
                                        <Form.Item label="信用代码" name="creditCode">
                                            <Input style={{ width: 260 }} />
                                        </Form.Item>
                                        <Form.Item label="行业分类" name="industry">
                                            <Input style={{ width: 200 }} />
                                        </Form.Item>
                                        <Form.Item label="注册类型" name="orgType">
                                            <Input style={{ width: 200 }} />
                                        </Form.Item>
                                    </Space>
                                    <Space size="large" wrap>
                                        <Form.Item label="注册区域" name="region">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="报告创建时间" name="reportCreated">
                                            <DatePicker />
                                        </Form.Item>
                                    </Space> 
                                    <Divider orientation="left">列表信息</Divider>
                                    {renderShareholderList()}
                                    <Divider />
                                    {renderLegalChangeList()}
                                    <Form.Item>
                                        <Button type="primary" onClick={handleBasicSave}>保存基本信息</Button>
                                    </Form.Item>
                                </Form>
                            </>
                        )
                    },
                    {
                        key: 'tax',
                        label: '纳税信息',
                        children: (
                            <Form layout="vertical" form={taxForm}>
                                <Form.Item name="id" hidden><Input /></Form.Item>
                                <Space size="large" wrap>
                                    <Form.Item label="纳税信用等级" name="taxCreditLevel">
                                        <Input style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="纳税人类型" name="taxpayerType">
                                        <Input style={{ width: 200 }} />
                                    </Form.Item>
                                </Space>
                                <Space size="large" wrap>
                                    <Form.Item label="近12月增值税销售额" name="taxVatSales12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近24月增值税销售额" name="taxVatSales24m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月欠税记录次数" name="taxArrearTimes">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月完税总额" name="taxPaid12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                </Space>
                                <Space size="large" wrap>
                                    <Form.Item label="当前欠税余额" name="taxArrearBalance">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近24月完税总额" name="taxPaid24m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近3个月滞纳金金额" name="taxLateFee3m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月应纳税额" name="taxVatPayable12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                </Space>
                                <Space size="large" wrap>
                                    <Form.Item label="近12个月滞纳金金额" name="taxLateFee12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月零申报月数" name="taxZeroMonths12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月滞纳金次数" name="taxLateFeeTimes">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="最长连续零申报(月)" name="taxZeroStreak12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                </Space>
                                <Space size="large" wrap>
                                    <Form.Item label="资产金额" name="assetAmount"><InputNumber style={{ width: 200 }} /></Form.Item>
                                    <Form.Item label="负债率" name="debtRatio"><InputNumber style={{ width: 200 }} /></Form.Item>
                                    <Form.Item label="营业利润" name="netProfit"><InputNumber style={{ width: 200 }} /></Form.Item>
                                    <Form.Item label="净利率" name="netProfitRatio"><InputNumber style={{ width: 200 }} /></Form.Item>
                                </Space>
                                <Form.Item label="滞纳金情况" name="taxLateFeeDesc">
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                                <Form.Item label="逾期提示" name="taxPenaltyTip">
                                    <Input.TextArea rows={2} />
                                </Form.Item>
                                {renderPenaltyList()}
                                {renderMonthlyList('taxMonthly')}
                                <Form.Item>
                                    <Button type="primary" onClick={handleTaxSave}>保存纳税信息</Button>
                                </Form.Item>
                            </Form>
                        )
                    },
                    {
                        key: 'invoice',
                        label: '发票信息',
                        children: (
                            <Form layout="vertical" form={invoiceForm}>
                                <Form.Item name="id" hidden><Input /></Form.Item>
                                <Space size="large" wrap> 
                                    <Form.Item label="近45日开票" name="invoiceRecentRecord">
                                        <Input style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近1月开票金额" name="invoiceMonthAmount">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近3月开票增长率" name="invoiceGrowth3m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近3月开票金额" name="invoiceAmount3m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                </Space>
                                <Space size="large" wrap>
                                    <Form.Item label="近6月增长率" name="invoiceGrowth6m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近6月开票金额" name="invoiceAmount6m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月增长率" name="invoiceGrowth12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月开票金额" name="invoiceAmount12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                </Space>
                                <Space size="large" wrap>
                                    <Form.Item label="近24月开票金额" name="invoiceAmount24m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近3月客户数量" name="invoiceCustomer3m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月客户数量" name="invoiceCustomer12m">
                                        <Input style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月开票张数" name="invoiceCount12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                </Space>
                                <Space size="large" wrap>
                                    <Form.Item label="作废比例(%)" name="invoiceVoidRatio12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="近12月月均开票金额增长率(%)" name="invoiceAvgGrowth12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="红冲数量占比(%)" name="invoiceRedRatioQty12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="红冲金额占比(%)" name="invoiceRedRatioAmt12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="最长连续断票(月)" name="invoiceBreakMonths12m">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="断票月数(除2月)" name="invoiceBreakMonthsExFeb">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                    <Form.Item label="最大断票天数" name="invoiceMaxGapDays">
                                        <InputNumber style={{ width: 200 }} />
                                    </Form.Item>
                                </Space>
                                {renderMonthlyList('invoiceMonthly')}
                                <Form.Item>
                                    <Button type="primary" onClick={handleInvoiceSave}>保存发票信息</Button>
                                </Form.Item>
                            </Form>
                        )
                    },
                    {
                        key: 'partner',
                        label: '供应商/客户',
                        children: (
                            <Form layout="vertical" form={partnerForm}>
                                <Form.Item name="id" hidden><Input /></Form.Item>
                                {renderPartnerList('suppliers', '供应商Top10')}
                                <Divider />
                                {renderPartnerList('customers', '客户Top10')}
                                <Form.Item>
                                    <Button type="primary" onClick={handlePartnerSave}>保存供应商/客户信息</Button>
                                </Form.Item>
                            </Form>
                        )
                    }
                ]}
            />
        </Card>
    );

    return (
        <div className="report-page">
            {columnSummary}
            {detailTabs}
        </div>
    );
};

